// PresenceActivity.js — met à jour MON activité (statut Nakama) selon la page où je suis,
// pour que mes amis voient « Parcourt les jeux / Regarde des vidéos / Dans un salon… ».
// + DÉTECTION D'INACTIVITÉ : actif → statut de la page ; inactif ≥ 2 min → « Inactif sur l'AppStore » ;
// ≥ 10 min → « Absent 💤 ». Changer d'onglet/page relance le compteur (on reste « en ligne » 2 min après avoir
// quitté la page, tant que l'onglet reste ouvert), puis on bascule inactif/absent si l'inactivité se prolonge.
import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { activityForPath, statusPayload } from '../services/activity';
import { loadPrefs, getPrefs, onPrefsChange } from '../services/preferences';
import { syncPush } from '../services/push';
import { track, trackAppOpen } from '../services/analytics';

const IDLE_INACTIVE_MS = 120000;   // 2 min sans activité → « Inactif sur l'AppStore »
const IDLE_AWAY_MS = 600000;       // 10 min sans activité → « Absent 💤 »

export default function PresenceActivity() {
  const { auth, isLoggedIn } = useAuth();
  const loc = useLocation();
  const lastActivityRef = useRef(Date.now());
  const idleStateRef = useRef('active');   // 'active' | 'inactive' | 'away'
  const applyRef = useRef(() => {});
  const settleTimer = useRef(null);        // debounce : statut PRÉCIS publié seulement après stabilité dans la section

  useEffect(() => { if (isLoggedIn) { loadPrefs(); syncPush(auth); } }, [isLoggedIn, auth]);
  useEffect(() => { trackAppOpen(); }, []);
  useEffect(() => { track('page_view', { extra: { path: loc.pathname } }); }, [loc.pathname]);

  // Publie le statut selon la page + la VISIBILITÉ choisie + l'ÉTAT D'INACTIVITÉ.
  const apply = () => {
    if (!isLoggedIn || !auth.enabled) return;
    const p = getPrefs().privacy || {};
    const vis = p.statusVisibility || 'detailed';
    // Inactivité : prioritaire sur l'activité de page (mais respecte showOnline/hidden).
    const st = idleStateRef.current;
    if ((st === 'inactive' || st === 'away') && p.showOnline !== false && vis !== 'hidden') {
      auth.updateStatus({ a: st === 'away' ? 'Absent 💤' : 'Inactif sur l\'AppStore', away: true });
      return;
    }
    if (p.status === 'away' && p.showOnline !== false && vis !== 'hidden') { auth.updateStatus({ a: 'Absent 💤', away: true }); return; }
    const payload = statusPayload(p, { a: activityForPath(loc.pathname) });
    if (payload === null) auth.updateStatus(''); else auth.updateStatus(payload);
  };
  applyRef.current = apply;

  // ÉTAT INTERMÉDIAIRE « En ligne » : publié pendant une navigation (transition, activité pas encore déterminée) →
  // le statut ne « saute » plus d'un précis à l'autre. On garde la priorité inactif/absent et le masquage.
  const applyGeneric = () => {
    if (!isLoggedIn || !auth.enabled) return;
    const p = getPrefs().privacy || {};
    const vis = p.statusVisibility || 'detailed';
    if (p.showOnline === false || vis === 'hidden') return;         // masqué → on ne publie rien
    const st = idleStateRef.current;
    if (st === 'inactive' || st === 'away' || p.status === 'away') { apply(); return; }   // inactif/absent = prioritaire
    auth.updateStatus({ a: 'En ligne' });
  };

  // À chaque changement de page : « En ligne » TOUT DE SUITE (stable), puis le statut PRÉCIS après ~1,5 s de stabilité
  // dans la section (annulé si on renavigue avant) → l'ami voit un statut stable, pas un clignotement de libellés.
  useEffect(() => {
    applyGeneric();
    if (settleTimer.current) clearTimeout(settleTimer.current);
    settleTimer.current = setTimeout(() => applyRef.current(), 1500);
    return () => { if (settleTimer.current) clearTimeout(settleTimer.current); };
  }, [auth, isLoggedIn, loc.pathname]);
  useEffect(() => onPrefsChange(() => applyRef.current()), []);

  // Suivi de l'activité + bascule inactif/absent. Un changement d'onglet (visibilitychange) compte comme une
  // activité → relance le compteur → on reste « en ligne » 2 min après avoir quitté la page.
  useEffect(() => {
    if (!isLoggedIn || !auth.enabled) return undefined;
    const bump = () => {
      lastActivityRef.current = Date.now();
      if (idleStateRef.current !== 'active') { idleStateRef.current = 'active'; applyRef.current(); }   // retour d'inactivité → réapplique tout de suite
    };
    const evs = ['mousemove', 'mousedown', 'keydown', 'touchstart', 'scroll', 'visibilitychange', 'focus'];
    evs.forEach((ev) => window.addEventListener(ev, bump, { passive: true }));
    const iv = setInterval(() => {
      const idle = Date.now() - lastActivityRef.current;
      const next = idle >= IDLE_AWAY_MS ? 'away' : idle >= IDLE_INACTIVE_MS ? 'inactive' : 'active';
      if (next !== idleStateRef.current) { idleStateRef.current = next; applyRef.current(); }
    }, 20000);
    return () => { evs.forEach((ev) => window.removeEventListener(ev, bump)); clearInterval(iv); };
  }, [auth, isLoggedIn]);

  return null;
}
